/**
 * UMD ID Card SVG components — front and back faces.
 * Used by the kiosk swipe animation and the card preview page.
 */
import React from "react";
import umdLogo from "./umd-logo.svg";

export const CARD_W = 180;
export const CARD_H = 113;

// Barcode as a single path (87 bars collapsed into one element)
const BARCODE_D = "M28.3,50h1.2v30h-1.2ZM30.3,50h0.4v30h-0.4ZM31.5,50h0.8v30h-0.8ZM33.0,50h1.2v30h-1.2ZM34.2,50h0.8v30h-0.8ZM36.1,50h0.4v30h-0.4ZM37.7,50h1.2v30h-1.2ZM38.9,50h0.4v30h-0.4ZM40.8,50h0.8v30h-0.8ZM42.4,50h1.2v30h-1.2ZM43.5,50h0.8v30h-0.8ZM44.7,50h0.4v30h-0.4ZM46.7,50h1.2v30h-1.2ZM47.8,50h0.4v30h-0.4ZM49.4,50h0.8v30h-0.8ZM50.6,50h1.2v30h-1.2ZM52.5,50h0.8v30h-0.8ZM54.1,50h0.4v30h-0.4ZM55.3,50h1.2v30h-1.2ZM56.4,50h0.4v30h-0.4ZM58.4,50h0.8v30h-0.8ZM59.5,50h1.2v30h-1.2ZM61.1,50h0.8v30h-0.8ZM62.3,50h0.4v30h-0.4ZM64.2,50h1.2v30h-1.2ZM65.8,50h0.4v30h-0.4ZM67.0,50h0.8v30h-0.8ZM68.1,50h1.2v30h-1.2ZM70.1,50h0.8v30h-0.8ZM71.2,50h0.4v30h-0.4ZM72.8,50h1.2v30h-1.2ZM74.0,50h0.4v30h-0.4ZM75.9,50h0.8v30h-0.8ZM77.5,50h1.2v30h-1.2ZM78.7,50h0.8v30h-0.8ZM79.8,50h0.4v30h-0.4ZM81.8,50h1.2v30h-1.2ZM82.9,50h0.4v30h-0.4ZM84.5,50h0.8v30h-0.8ZM85.7,50h1.2v30h-1.2ZM87.6,50h0.8v30h-0.8ZM89.2,50h0.4v30h-0.4ZM90.4,50h1.2v30h-1.2ZM91.5,50h0.4v30h-0.4ZM93.5,50h0.8v30h-0.8ZM94.6,50h1.2v30h-1.2ZM96.2,50h0.8v30h-0.8ZM97.4,50h0.4v30h-0.4ZM99.3,50h1.2v30h-1.2ZM100.9,50h0.4v30h-0.4ZM102.0,50h0.8v30h-0.8ZM103.2,50h1.2v30h-1.2ZM105.2,50h0.8v30h-0.8ZM106.3,50h0.4v30h-0.4ZM107.9,50h1.2v30h-1.2ZM109.1,50h0.4v30h-0.4ZM111.0,50h0.8v30h-0.8ZM112.6,50h1.2v30h-1.2ZM113.8,50h0.8v30h-0.8ZM114.9,50h0.4v30h-0.4ZM116.9,50h1.2v30h-1.2ZM118.0,50h0.4v30h-0.4ZM119.6,50h0.8v30h-0.8ZM120.8,50h1.2v30h-1.2ZM122.7,50h0.8v30h-0.8ZM124.3,50h0.4v30h-0.4ZM125.5,50h1.2v30h-1.2ZM126.6,50h0.4v30h-0.4ZM128.6,50h0.8v30h-0.8ZM129.7,50h1.2v30h-1.2ZM131.3,50h0.8v30h-0.8ZM132.5,50h0.4v30h-0.4ZM134.4,50h1.2v30h-1.2ZM136.0,50h0.4v30h-0.4ZM137.2,50h0.8v30h-0.8ZM138.3,50h1.2v30h-1.2ZM140.3,50h0.8v30h-0.8ZM141.4,50h0.4v30h-0.4ZM143.0,50h1.2v30h-1.2ZM144.2,50h0.4v30h-0.4ZM146.1,50h0.8v30h-0.8ZM147.7,50h1.2v30h-1.2ZM148.9,50h0.8v30h-0.8ZM150.0,50h0.4v30h-0.4ZM152.0,50h1.2v30h-1.2ZM153.1,50h0.4v30h-0.4ZM154.7,50h0.8v30h-0.8Z";

export const CardFront = React.memo(function CardFront({ uid = "" }) {
  const clipId = uid ? `photo-clip-${uid}` : "photo-clip";
  const w = CARD_W, h = CARD_H;
  const logoW = 34;
  const barStart = logoW;
  const barEnd = 140;
  const pW = 50, pH = 72;
  const photoX = barStart;
  const rC = barStart + pW + 4;
  const topBarY = 20;
  const photoY = topBarY + 3;
  const botBarY = 97;

  return (
    <g>
      <rect width={w} height={h} rx={4} fill="white" stroke="#ccc" strokeWidth={0.5} />

      {/* UMD logo */}
      <image
        href={umdLogo}
        x={0} y={0}
        width={80} height={13}
        transform={`rotate(-90, ${logoW / 2}, ${h / 2}) translate(${logoW / 2 - 40}, ${h / 2 - 6.5})`}
      />

      {/* Name */}
      <text x={barStart} y={16} fill="#111" fontSize={9.5} fontWeight="800"
        fontFamily="'Helvetica Neue',Helvetica,Arial,sans-serif">Testudo</text>

      {/* Top red bar */}
      <rect x={barStart} y={topBarY} width={170 - barStart} height={1.2} fill="#E21833" />

      {/* Photo with avatar, clipped */}
      <defs>
        <clipPath id={clipId}>
          <rect x={photoX} y={photoY} width={pW} height={pH} rx={2} />
        </clipPath>
      </defs>
      <g clipPath={`url(#${clipId})`}>
        <rect x={photoX} y={photoY} width={pW} height={pH} rx={2} fill="#E21833" />
        <ellipse cx={photoX + pW / 2} cy={photoY + pH} rx={pW * 0.42} ry={pH * 0.42} fill="#eec8a0" />
        <circle cx={photoX + pW / 2} cy={photoY + pH - pH * 0.42 - pW * 0.17 - 2} r={pW * 0.17} fill="#eec8a0" />
      </g>

      {/* Classification */}
      <text x={rC} y={36} fill="#E21833" fontSize={7} fontWeight="600"
        fontFamily="'Helvetica Neue',Helvetica,Arial,sans-serif">Undergraduate</text>
      <text x={rC} y={45} fill="#E21833" fontSize={7} fontWeight="600"
        fontFamily="'Helvetica Neue',Helvetica,Arial,sans-serif">Student</text>

      {/* Signature */}
      <g fill="none" stroke="#1a1a3a" strokeLinecap="round" strokeLinejoin="round">
        <path strokeWidth={0.55} d="M 88,65.5 C 87.3,60 87,55 88.5,51.5 C 89.5,49 91.5,48.5 93.5,49.5 C 95.5,50.5 96,52.5 95,55 C 94,57.5 92,61 90.5,63.5 C 89.5,65.5 89.5,67 91,66.5 C 92,66 93.2,64.2 94.2,63 C 95,62 95.8,61.5 96.2,62.5 C 96.6,63.5 95.5,65 94.5,65.8 C 93.5,66.5 93.5,66 95,65 C 96,64.2 97,63.5 97.8,63 C 98.5,62.5 99,62.5 99,63.2 C 99,64 98.2,65.2 98,65.8 C 97.8,66.3 98.5,66 99.5,65.2 C 100.3,64.5 101,63.5 101.8,62.8 C 102.3,62.3 102.8,62 103,62.8 C 103.2,63.5 102.8,64.8 102.5,65.5 C 102.2,66.2 102.8,66 103.8,65.2 C 104.5,64.5 105,63.5 105.5,62 C 106,60.5 106.5,59.5 106.8,60 C 107,60.5 106.8,62 106.5,63.5 C 106.2,65 106.5,66 107.5,65.5 C 108.2,65 109,63.5 109.8,62.5 C 110.3,62 110.8,62 111,62.8 C 111.2,63.5 110.5,65 110.5,65.5 C 110.5,66 111,65.8 112,65 C 113,64 113.8,62 114.8,59.5 C 115.5,57.2 116.5,55 117.2,53.5 C 117.8,52 118.2,52 118.2,53.5 C 118.2,55 117.5,58 117,60.5 C 116.5,63 116.2,65 117,65.5 C 117.8,66 118.5,65 119.5,64 C 120.2,63.2 121,62.5 121.5,62.5 C 122,62.5 122.2,63.2 121.5,64.2 C 120.8,65.2 120,65.8 120.5,66 C 121,66.2 122,65.5 123.5,64.8 C 125,64 127,63.2 129.5,63 C 131.5,62.8 133,63.5 133,64.2" />
        <path strokeWidth={0.45} d="M 89.5,53 C 91,52.5 93.5,52.2 96,52.8 C 97,53 97.3,53.3 96.8,53.4" />
        <path strokeWidth={0.4} d="M 104.8,61 C 105.5,60.8 106.5,60.8 107.5,61.2" />
      </g>

      {/* U ID */}
      <text x={rC} y={photoY + pH - 12} fill="#111" fontSize={5.5} fontWeight="600"
        fontFamily="'Helvetica Neue',Helvetica,Arial,sans-serif">U ID: 123456789</text>

      {/* Issued date */}
      <text x={rC} y={photoY + pH - 4} fill="#777" fontSize={4.5}
        fontFamily="'Helvetica Neue',Helvetica,Arial,sans-serif">Issued on: 08/30/2021</text>

      {/* Bottom red bar */}
      <rect x={barStart} y={botBarY} width={barEnd - barStart} height={1.2} fill="#E21833" />

      {/* Maryland M */}
      <text x={143} y={botBarY - 12} fill="#E21833" fontSize={20} fontWeight="900"
        fontFamily="Georgia,'Times New Roman',serif"
        transform="rotate(45, 143, 83)">M</text>
    </g>
  );
});

export const CardBack = React.memo(function CardBack() {
  const w = CARD_W, h = CARD_H;
  return (
    <g>
      <rect width={w} height={h} rx={4} fill="white" stroke="#ccc" strokeWidth={0.5} />
      {/* Magnetic stripe */}
      <rect x={0} y={16} width={w} height={22} fill="#1a1a1a" />
      <rect x={0} y={38} width={w} height={5} fill="#3b342e" />
      {/* Barcode — single path element */}
      <path d={BARCODE_D} fill="#1a1a1a" />
      {/* Barcode number */}
      <text x={w / 2} y={84} fill="#333" fontSize={4.5} fontWeight="500"
        fontFamily="'Courier New',monospace" textAnchor="middle">9 012345 678901</text>
      {/* Fine print */}
      <rect x={14} y={96} width={w * 0.55} height={1.2} rx={0.5} fill="#d0d0d0" />
      <rect x={14} y={100} width={w * 0.4} height={1.2} rx={0.5} fill="#d0d0d0" />
    </g>
  );
});
